import { gql, type TypedDocumentNode } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconPlayCircle } from '@sorare/core/src/atoms/icons/system/iconPlayCircle.ts';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { ANY_SPORT_MARKET_WHEEL_RARITY } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { isType } from '@sorare/core/src/lib/gql';
import { useWheelTickets } from '@sorare/marketplace/src/hooks/wheel/useWheelTickets';

import { DailyActionTask } from '#src/components/missions/DailyActionTask/index.tsx';
import { FeaturedCollectionTask } from '#src/components/missions/FeaturedCollectionTask/index.tsx';

import type { SeriesMissions_currentUser } from './__generated__/index.graphql.ts';

type Props = {
  currentUser: SeriesMissions_currentUser;
  showFeaturedCollectionTask: boolean;
};
export const SeriesMissions = ({
  currentUser,
  showFeaturedCollectionTask,
}: Props) => {
  const { ticketCount } = useWheelTickets();
  const { generateSportContextPath } = useSportContext();

  const playDailyActionTask = isType(
    currentUser?.dailyRunTask,
    'DailyActionTask'
  )
    ? currentUser?.dailyRunTask
    : null;
  const featuredCollectionTask = currentUser?.setCollectionTasks
    ?.filter(
      task =>
        isType(task, 'CompleteCollectionsCountTask') ||
        isType(task, 'CardsCountTask')
    )
    .at(0);

  if (!playDailyActionTask && !featuredCollectionTask) {
    return null;
  }

  return (
    <Vertical gap={2}>
      {!!playDailyActionTask && (
        <DailyActionTask
          task={playDailyActionTask}
          onDecisivePlayerPickerClick={() => {}}
          onDropChallengeClick={() => {}}
        />
      )}
      {!!featuredCollectionTask && !!showFeaturedCollectionTask && (
        <FeaturedCollectionTask task={featuredCollectionTask} />
      )}
      <Button
        size="medium"
        color={ticketCount > 0 ? 'secondary' : 'quaternary'}
        to={generateSportContextPath(ANY_SPORT_MARKET_WHEEL_RARITY, {
          params: {
            rarity: Rarity.common,
          },
        })}
        disabled={ticketCount === 0}
      >
        <FontAwesomeIcon icon={iconPlayCircle} />
        <FormattedMessage defaultMessage="Spin the wheel" />
      </Button>
    </Vertical>
  );
};

SeriesMissions.fragments = {
  currentUser: gql`
    fragment SeriesMissions_currentUser on CurrentUser {
      slug
      dailyRunTask(sport: $sport) {
        id
        name
        aasmState
        ... on DailyActionTask {
          id
        }
        ...DailyActionTask_taskInterface
      }
      setCollectionTasks(sport: $sport) {
        id
        aasmState
        ...FeaturedCollectionTask_taskInterface
      }
    }
    ${DailyActionTask.fragments.taskInterface}
    ${FeaturedCollectionTask.fragments.taskInterface}
  ` as TypedDocumentNode<SeriesMissions_currentUser>,
};
